import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';

/**
 * Shared Material text field. Unlike the first (reverted) attempt, NOTHING is
 * content-projected: mat-label, the input, the password-visibility suffix,
 * mat-hint, and every mat-error are declared in THIS template, because
 * MatFormField locates them via content queries that cannot see nodes
 * re-projected through <ng-content> (see docs/store-offer-componentization.md
 * §36). All variability is expressed through typed inputs instead.
 *
 * Example (login password field):
 *   <app-mat-input
 *     [control]="$any(password)"
 *     label="admin.global.password"
 *     inputId="login-password-field"
 *     [passwordToggle]="true"
 *     suffixClass="pwdicon-top"
 *     errorSuffix="."
 *     [errors]="{
 *       required: 'admin.global.field.required',
 *       minlength: 'admin.global.field.minLength',
 *       maxlength: 'admin.global.field.maxLength',
 *     }"
 *   ></app-mat-input>
 *
 * Error messages are translate keys; the matched control error object is
 * passed as translate params (with `requiredLength` aliased to
 * `minLength`/`maxLength` so the existing keys keep working).
 */
@Component({
  selector: 'app-mat-input',
  templateUrl: './mat-input.component.html',
  styleUrls: ['./mat-input.component.scss'],
})
export class MatInputComponent {
  /** Translate key for the floating mat-label. Empty renders no label. */
  @Input() label = '';
  /** Translate key for the placeholder. */
  @Input() placeholder = '';
  /** Translate key for the mat-hint under the field. Empty renders no hint. */
  @Input() hint = '';
  /** Reactive control. Omit for a display-only field driven by `value`. */
  @Input() control?: FormControl;
  /** Display-only mode: static value rendered in a disabled input (no control). */
  @Input() value = '';
  @Input() type = 'text';
  @Input() inputId = '';
  @Input() testId = '';
  @Input() autocomplete = '';
  @Input() readonly = false;
  /** mat-form-field appearance — 'outline' (default), 'standard', 'fill', … */
  @Input() appearance = 'outline';
  /** mat-form-field floatLabel behaviour. */
  @Input() floatLabel = 'auto';
  /** Extra classes for the mat-form-field (e.g. 'mat-form-field-custom', 'no-padding-top'). */
  @Input() fieldClass = '';
  /** Render the visibility-toggle eye as matSuffix; the component owns the hide state. */
  @Input() passwordToggle = false;
  /** Extra classes for the suffix toggle button (e.g. 'pwdicon-top'). */
  @Input() suffixClass = '';
  /** errorKey (from control.errors) → translate key for its mat-error. */
  @Input() errors: Record<string, string> = {};
  /** Literal text appended after every error message (login uses '.'). */
  @Input() errorSuffix = '';
  /**
   * Fill the parent column (the default — what the auth screens' full-width
   * classes did). Pass false for fields that historically rendered at
   * Material's intrinsic width (printer popups, booking dialog).
   */
  @Input() fullWidth = true;
  @Output() blurEvent = new EventEmitter<FocusEvent>();
  @Output() keyupEvent = new EventEmitter<KeyboardEvent>();

  hide = true;

  @HostBinding('class.mat-input-full-width')
  get fullWidthClass(): boolean {
    return this.fullWidth;
  }

  get errorKeys(): string[] {
    return Object.keys(this.errors);
  }

  /** First configured error currently present on the control (Material shows one error at a time). */
  get activeErrorKey(): string {
    if (!this.control) {
      return '';
    }
    return this.errorKeys.find((key) => this.control.hasError(key)) || '';
  }

  get inputType(): string {
    if (this.passwordToggle) {
      return this.hide ? 'password' : 'text';
    }
    return this.type;
  }

  errorParams(key: string): Record<string, unknown> {
    const err = this.control ? this.control.getError(key) : null;
    if (!err || typeof err !== 'object') {
      return {};
    }
    return {
      ...err,
      minLength: err.requiredLength,
      maxLength: err.requiredLength,
    };
  }
}
